/**
 * Screen-scoped geometry tokens for the in-person signing hand-off shell.
 * Colours are NOT stored here — they come from `theme.palette.signer.*` so the
 * shell blends with the signing surface it overlays (see
 * `InPersonSigning.styles.ts`). Only layout constants live here, per
 * UI_IMPLEMENTATION_RULES § Temporary Screen Tokens.
 */
export const inPersonTokens = {
  /** Full-screen interstitial / completion / summary overlay. */
  overlayZ: 1300,
  /** Floating "Return to host" cluster shown during signing. */
  barZ: 1250,

  // Sizing below matches the "In-Person Signing - Mobile & Tablet" design
  // (Claude Design project 23d01a63…, InPersonScreen.dc.html) — its `compact`
  // vs wide split is `width <= 400`, which lines up with this app's `sm`
  // breakpoint closely enough to reuse directly.
  card: {
    maxWidth: 468,
    radius: "16px",
    padding: "32px 18px",
    paddingWide: "44px 30px",
    gap: "16px",
  },

  iconCircle: 58,
  iconGlyph: 26,

  title: {
    size: "20px",
    weight: 800,
    lineHeight: 1.25,
    letterSpacing: "-0.01em",
  },
  subtitle: {
    size: "13px",
    lineHeight: 1.65,
  },
  signerChip: {
    radius: "999px",
    padding: "5px 13px",
    labelSize: "10.5px",
    nameSize: "12.5px",
  },

  /** Consent acknowledgement (host capture dialog + signer hand-off card). */
  consent: {
    radius: "10px",
    padding: "10px 11px",
    gap: "8px",
    labelSize: "11.5px",
    lineHeight: 1.5,
    errorSize: "12px",
    checkboxSize: 16,
  },

  /**
   * Hand-off picker on the launch card — the host choosing between several
   * in-person signers the server reports as actionable at the same time
   * (a parallel signing level).
   */
  picker: {
    radius: "12px",
    padding: "12px",
    gap: "8px",
    optionRadius: "10px",
    optionPadding: "10px 12px",
    labelSize: "10.5px",
    nameSize: "13.5px",
    metaSize: "11.5px",
  },

  /** "Waiting on" recipient chips on the not-ready card. */
  waiting: {
    gap: "6px",
    labelSize: "10.5px",
    chipRadius: "999px",
    chipPadding: "4px 10px",
    chipSize: "12px",
  },

  /** "Would you like a copy?" field on the completion screen. */
  copy: {
    radius: "12px",
    padding: "14px",
    gap: "10px",
    promptSize: "14px",
  },

  // Full-width hero CTAs (landing "Continue", consent "Agree & start
  // signing", completion "Continue", summary "Back to request").
  primaryButton: {
    heightMobile: 46,
    height: 50,
    radius: "10px",
    fontSize: "14.5px",
    fontWeight: 700,
  },
  ghostButton: {
    fontSize: "12.5px",
    fontWeight: 600,
  },
  // The capture modal's own two-up footer (Cancel outlined + Start session
  // filled) — same height as the hero buttons, smaller type since they sit
  // side by side rather than standing alone.
  modalButton: {
    fontSize: "13.5px",
    fontWeight: 700,
    gap: "10px",
  },

  bar: {
    radius: "14px",
    padding: "10px 12px 10px 16px",
    gap: "14px",
    offset: 20,
    labelSize: "11px",
    nameSize: "13.5px",
    buttonHeight: 38,
    buttonRadius: "10px",
    buttonFontSize: "13px",
    // Below `md`: collapses to `barCompact`, a small top-right icon button
    // instead of the full bottom-left pill — see `styles.bar`/`barCompact`.
    offsetMobile: 8,
    compactSize: 40,
    compactIconSize: 18,
  },
} as const;

export default inPersonTokens;
